import React, { FC, useState, useEffect } from 'react';
import {
    DndContext,
    type DndContextProps,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
    DragOverEvent,
    DragStartEvent,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    horizontalListSortingStrategy,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import SortableItemComponent from './sortable-item.component';
import * as styles from './sortable.component.module.scss';
import { OptionalChildrenProps } from '../../../types/react.types';

interface SortableComponentProps extends OptionalChildrenProps {
    items: { uuid: string, [key: string]: any }[];
    sortHandlerClass: string;
    classes?: string[];
    orderChanged?: (newOrder: any[]) => void;
    sortEnded: (newOrder: any[]) => void;
    /**
     * Which way the list runs. Default 'vertical' -- the shape every existing caller has.
     *
     * The Studio's timeline is a row of clips, and dnd-kit needs the matching strategy or the drop
     * indicator is computed against the wrong axis: items jump a row instead of a column. Additive
     * because the alternative was a second copy of this component, and the owner's rule is one
     * dnd-kit wrapper for the whole site (2026-09-11).
     */
    strategy?: 'vertical' | 'horizontal';
    /**
     * Classes for the wrapper dnd-kit puts around each child. Default `col-12 mb-2 g-0`, which is
     * the bootstrap row shape the converter's file list is built on and is unusable for a
     * horizontal track (full width, one per line).
     */
    itemClassName?: string;
    /**
     * Whether to render the drag-handle image. Default true.
     *
     * With `false` the child is rendered alone and the drag ACTIVATORS are spread onto the item
     * wrapper -- the whole tile becomes the handle, which is what an 84 px clip wants and what a
     * 16 px grip icon over a photo would spoil.
     */
    showHandle?: boolean;
    /**
     * Optional drag lifecycle, for a caller that draws its own drop indicator (the Studio's
     * timeline, round 6). Additive: every existing caller passes none of these.
     */
    /** A drag began on the item with this uuid. */
    dragStarted?: (id: string) => void;
    /** The dragged item is over another item (or over none); fires on every change. */
    dragOver?: (activeId: string, overId: string | null) => void;
    /** The drag ended or was cancelled, whether or not the order changed. */
    dragEnded?: () => void;
    /**
     * dnd-kit's auto-scroll options, passed straight through to `DndContext`. Additive: absent, dnd-kit
     * keeps its default (every scrollable ancestor, including the page). The Studio's timeline passes a
     * `canScroll` that names its ONE horizontal scroller, because the default scrolled the page under a
     * drag whose tile sat in the viewport's bottom fifth (round 12, N2, O4).
     */
    autoScroll?: DndContextProps['autoScroll'];
}

const SortableComponent: FC<SortableComponentProps> = (props) => {
    const { items, children, sortEnded, classes } = props;
    // We need to map items to their IDs for SortableContext
    const [activeId, setActiveId] = useState<string | null>(null);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5, // Wait 5px movement before drag starts to prevent accidental clicks
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    function handleDragStart(event: DragStartEvent) {
        setActiveId(String(event.active.id));
        props.dragStarted?.(String(event.active.id));
    }

    function handleDragOver(event: DragOverEvent) {
        props.dragOver?.(String(event.active.id), event.over ? String(event.over.id) : null);
    }

    function handleDragCancel() {
        setActiveId(null);
        props.dragEnded?.();
    }

    function handleDragEnd(event: DragEndEvent) {
        const { active, over } = event;
        setActiveId(null);
        props.dragEnded?.();

        if (active.id !== over?.id) {
            const oldIndex = items.findIndex((item) => item.uuid === active.id);
            const newIndex = items.findIndex((item) => item.uuid === over?.id);

            if (oldIndex !== -1 && newIndex !== -1) {
                const newOrder = arrayMove(items, oldIndex, newIndex);
                sortEnded(newOrder);
            }
        }
    }

    // Separate sortable and non-sortable children
    const childArray = React.Children.toArray(children);
    const sortableChildren = childArray.filter(c => !c['props']['data-notsortable']);
    const nonSortableChildren = childArray.filter(c => c['props']['data-notsortable']);

    return (
        <div id="sortable" className={`${(classes || []).join(" ")}`}>
            {nonSortableChildren.map((child, index) => (
                <div key={`non-sortable-${index}`} className={styles['non-sortable-item']} style={{ display: 'inline-block' }}>
                    {child}
                </div>
            ))}

            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                {...(props.autoScroll !== undefined ? { autoScroll: props.autoScroll } : {})}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragCancel={handleDragCancel}
                onDragEnd={handleDragEnd}
            >
                <SortableContext
                    items={items.map(item => item.uuid)}
                    strategy={props.strategy === 'horizontal' ? horizontalListSortingStrategy : verticalListSortingStrategy}
                >
                    {sortableChildren.map((child, index) => {
                        // We must find the correct item UUID that matches this child.
                        // Assuming strict 1:1 mapping based on index from the parent props.items
                        const item = items[index];
                        if (!item) return null;

                        return (
                            <SortableItemComponent
                                key={item.uuid}
                                id={item.uuid}
                                index={index}
                                className={props.itemClassName}
                                showHandle={props.showHandle}
                            >
                                {child}
                            </SortableItemComponent>
                        );
                    })}
                </SortableContext>
            </DndContext>
        </div>
    );
};

export default SortableComponent;
